import { useEffect, useRef } from 'react';

interface AdSenseProps {
  adSlot: string;
  adFormat?: 'auto' | 'fluid' | 'rectangle' | 'vertical' | 'horizontal';
  adLayout?: string;
  adStyle?: React.CSSProperties;
  className?: string;
  fullWidthResponsive?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: Array<Record<string, unknown>>;
  }
}

export default function AdSense({
  adSlot,
  adFormat = 'auto',
  adLayout,
  adStyle = { display: 'block' },
  className = '',
  fullWidthResponsive = true,
}: AdSenseProps) {
  const adRef = useRef<HTMLModElement>(null);
  const isAdPushed = useRef(false);

  useEffect(() => {
    // Only push the ad once
    if (isAdPushed.current) return;
    
    try {
      // Initialize adsbygoogle array if it doesn't exist
      if (typeof window !== 'undefined') {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
        isAdPushed.current = true;
      }
    } catch (error) {
      console.error('AdSense error:', error);
    }
  }, []);

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className}`}
      style={adStyle}
      data-ad-client="ca-pub-1335949711858877"
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-ad-layout={adLayout}
      data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
    />
  );
}

// Pre-configured ad components for common use cases
export function DisplayAd({ className = '' }: { className?: string }) {
  return (
    <AdSense
      adSlot="your-display-ad-slot-id"
      adFormat="auto"
      className={className}
      fullWidthResponsive={true}
    />
  );
}

export function InArticleAd({ className = '' }: { className?: string }) {
  return (
    <AdSense
      adSlot="your-in-article-ad-slot-id"
      adFormat="fluid"
      adLayout="in-article"
      className={className}
      fullWidthResponsive={true}
    />
  );
}

export function SquareAd({ className = '' }: { className?: string }) {
  return (
    <AdSense
      adSlot="your-square-ad-slot-id"
      adFormat="rectangle"
      className={className}
      adStyle={{ display: 'block', width: '300px', height: '250px' }}
      fullWidthResponsive={false}
    />
  );
}

export function HorizontalAd({ className = '' }: { className?: string }) {
  return (
    <AdSense
      adSlot="your-horizontal-ad-slot-id"
      adFormat="horizontal"
      className={className}
      fullWidthResponsive={true}
    />
  );
}
